'use client';
import Hamburger from '../../resources/assets/hamburger.svg';
import MoveBack from '../../resources/assets/moveBack.svg';
import Delete from '../../resources/assets/delete.svg';
import Image from 'next/image';
import { BoxComponent, ButtonComponent } from '@zeus/web-components';
import { styles } from './appbar.styles';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { sendGAEvent } from '@next/third-parties/google';
import { EventAction, EventType } from '../../config/GAConstants';
import { useTheme } from '@mui/material';
import { CookieValueTypes, getCookie } from 'cookies-next';
import { ViewCartModal } from './ViewCartModal';
import { sendEventToPostHog } from '../../utils/UtilFunctions';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { dynamicBusinessRoute } from '../../config/routes';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { updateGiftCardSettings } from '@store/online/giftCards/giftCardSlice';
import { useDispatch } from 'react-redux';
/**
 * Navbar component.
 *
 *  {React.Dispatch<React.SetStateAction<boolean>>} setOpen - Function to control the open state of the SideBar.
 *  {string} title - Title to display in the app bar.
 *  {() => void} handleBack - Callback function for handling the back action.
 *  {() => void} handleDelete - Callback function for handling the delete action.
 *  {boolean} isDeleteAble - Determines whether the delete button should be shown.
 */
const Appbar = ({
  setOpen,
  title,
  isDeleteAble,
  handleBack,
  handleDelete,
  isInitial,
  handleClick,
  step,
  isSignup,
  parentPage,
  webNavbarOnMobile = false,
  containerStyle,
  textContainer,
}: {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  title?: string;
  handleBack?: () => void;
  handleDelete?: () => void;
  isDeleteAble?: boolean;
  isInitial?: boolean;
  handleClick?: () => void;
  step?: number;
  isSignup?: boolean;
  parentPage?: string;
  webNavbarOnMobile?: boolean;
  containerStyle?: any;
  textContainer?: any;
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const theme = useTheme();
  const [token, setToken] = useState<CookieValueTypes>(); // Initialize token as undefined
  const router = useRouter();
  const [isCartModalOpen, setIsCartModalOpen] = useState<boolean>(false);
  const accessToken = getCookie('AccessToken');

  const dispatch = useDispatch();

  useEffect(() => {
    setToken(accessToken); // Update state
  }, [token, accessToken]);

  const handleOpen = useCallback(() => {
    if (setOpen) {
      setOpen((prev) => !prev); // Using "!" to assert that setOpen is not undefined
    }
  }, [setOpen]);

  const handleRedirect = () => {
    if (parentPage && parentPage === 'gift-card') {
      const currentPath = `${pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
      dispatch(updateGiftCardSettings({ isGiftCard: true, toRedirect: currentPath }));
    }
  };

  const Responsivestyles = {
    mobNavContainer: {
      ...(webNavbarOnMobile
        ? { display: 'none' }
        : {
            [theme.breakpoints.up('md')]: {
              display: 'none',
            },
          }),
    },
    deskNavContainer: {
      [theme.breakpoints.down('md')]: {
        display: webNavbarOnMobile ? 'block' : 'none',
      },
      padding: '16px 20px',
    },
  };

  const handleLogoClick = () => {
    const route = dynamicBusinessRoute();
    if (route) {
      router.push(route);
    }
  };

  if (isInitial) {
    return (
      <BoxComponent sx={styles.appbarContainer}>
        <Image
          id="appbar-logo"
          src="/assets/Logo.svg"
          alt="logo"
          width={57}
          height={31}
          style={{ cursor: 'pointer' }}
          onClick={handleLogoClick}
        />
        <BoxComponent sx={{ display: 'flex' }}>
          <Link href="/login">
            <ButtonComponent
              id="appbar-login-button"
              variant="outlined"
              data-testid="ls-login-btn"
              style={{ marginRight: '15px', width: '84px' }}
              onClick={() => {
                sendGAEvent(EventType.EVENT, EventAction.OL_LOGIN_SIGNUP_NAVBAR_LOGIN);
                sendEventToPostHog(EventType.EVENT, EventAction.OL_LOGIN_SIGNUP_NAVBAR_LOGIN);
                handleRedirect();
              }}
            >
              Log In
            </ButtonComponent>
          </Link>
          <Link href="/signup">
            <ButtonComponent
              id="appbar-signup-button"
              data-testid={parentPage === 'restaurant' ? 'ls-restaurant-signup-btn' : null}
              variant="contained"
              sx={{ width: '84px' }}
              onClick={() => {
                sendGAEvent(EventType.EVENT, EventAction.OL_LOGIN_SIGNUP_NAVBAR_SIGNUP);
                sendEventToPostHog(EventType.EVENT, EventAction.OL_LOGIN_SIGNUP_NAVBAR_SIGNUP);
                handleRedirect();
              }}
            >
              Sign Up
            </ButtonComponent>
          </Link>
          {isCartModalOpen && (
            <ViewCartModal isCartModalOpen={isCartModalOpen} setIsCartModalOpen={setIsCartModalOpen} />
          )}
        </BoxComponent>
      </BoxComponent>
    );
  }
  return (
    <>
      <BoxComponent sx={Responsivestyles.mobNavContainer} style={containerStyle}>
        <BoxComponent style={textContainer} sx={!title ? styles.headContainer : styles.headContainerBox}>
          {/* Hamburger or MoveBack icon based on the presence of setOpen */}
          {(!isSignup || step !== 2) && (
            <>
              {setOpen && !title ? (
                <Image
                  src={Hamburger}
                  alt="hamburger"
                  id="appbar-hamburger-icon"
                  onClick={handleOpen}
                  data-testid={'rp-side-drawer'}
                />
              ) : (
                <Image
                  src={MoveBack}
                  alt="moveback"
                  id="appbar-moveback-icon"
                  onClick={handleBack}
                  style={{ position: 'absolute' }}
                />
              )}
            </>
          )}

          {/* Logo or Title based on the presence of title */}
          <BoxComponent
            sx={title ? { ...styles.logoContainer, width: '100%' } : { ...styles.logoContainer, width: '80%' }}
          >
            {!title ? (
              <Image
                id="appbar-logo"
                src="/assets/Logo.svg"
                width={57}
                height={31}
                alt="logo"
                onClick={handleLogoClick}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <p style={styles.AppBarTitle}>{title}</p>
            )}
          </BoxComponent>

          {setOpen && !title && <BoxComponent sx={{ width: '40px' }} />}

          {/* Delete icon based on the value of isDeleteAble */}
          {isDeleteAble && <Image src={Delete} alt="moveback" id="appbar-delete-icon" onClick={handleDelete} />}
        </BoxComponent>
      </BoxComponent>

      <BoxComponent sx={Responsivestyles.deskNavContainer}>
        <BoxComponent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BoxComponent sx={{ cursor: 'pointer' }}>
            <Image
              id="appbar-logo"
              src="/assets/Logo.svg"
              width={57}
              height={31}
              alt="logo"
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
            />
          </BoxComponent>
          <BoxComponent sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <BoxComponent>
              {!token ? (
                <>
                  <BoxComponent>
                    <Link href="/login">
                      <ButtonComponent
                        id="appbar-login-button"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        onClick={() => {
                          sendGAEvent(EventType.EVENT, EventAction.OL_LOGIN_SIGNUP_NAVBAR_LOGIN);
                          handleRedirect();
                        }}
                      >
                        Login
                      </ButtonComponent>
                    </Link>
                    <Link href="/signup">
                      <ButtonComponent
                        id="appbar-signup-button"
                        variant="contained"
                        onClick={() => {
                          sendGAEvent(EventType.EVENT, EventAction.OL_LOGIN_SIGNUP_NAVBAR_SIGNUP);
                          handleRedirect();
                        }}
                      >
                        Signup
                      </ButtonComponent>
                    </Link>
                  </BoxComponent>
                </>
              ) : (
                <>
                  <AccountCircleOutlinedIcon
                    id="appbar-account-icon"
                    fontSize="large"
                    sx={{ width: '35px', height: '35px', color: 'primary.dark', cursor: 'pointer' }}
                    onClick={handleOpen}
                  />
                </>
              )}
            </BoxComponent>
          </BoxComponent>
        </BoxComponent>
        {!isCartModalOpen && (
          <ViewCartModal isCartModalOpen={isCartModalOpen} setIsCartModalOpen={setIsCartModalOpen} />
        )}
      </BoxComponent>
    </>
  );
};

export default Appbar;
